import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Flame, Utensils, Heart, Star, Users, Award, Clock } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const Highlights = () => {
    const { t } = useLanguage();
    const [ref, inView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    const highlights = [
        {
            icon: Flame,
            title: t('highlights.tandoori.title'),
            description: t('highlights.tandoori.description'),
            image: '/images/highlight_3.jpg',
        },
        {
            icon: Utensils,
            title: t('highlights.curries.title'),
            description: t('highlights.curries.description'),
            image: '/images/highlight_2.jpg',
        },
        {
            icon: Heart,
            title: t('highlights.ambiance.title'),
            description: t('highlights.ambiance.description'),
            image: '/images/highlight_1.jpg',
        },
    ];

    return (
        <section className="relative section-padding bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 overflow-hidden">
            {/* Background Decorations */}
            <div className="absolute inset-0">
                <div className="absolute top-20 left-10 w-40 h-40 bg-primary-200 dark:bg-primary-800 rounded-full opacity-5 blur-xl"></div>
                <div className="absolute bottom-20 right-10 w-56 h-56 bg-accent-200 dark:bg-accent-800 rounded-full opacity-5 blur-xl"></div>
                <div className="absolute top-1/2 left-1/3 w-32 h-32 bg-secondary-200 dark:bg-secondary-600 rounded-full opacity-3 blur-xl"></div>
            </div>

            <div className="relative container mx-auto max-w-7xl container-padding">
                {/* Enhanced Header Section */}
                <motion.div
                    ref={ref}
                    initial={{ opacity: 0, y: 50 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.8 }}
                    className="text-center mb-20"
                >
                    {/* Badge */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={inView ? { opacity: 1, scale: 1 } : {}}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="inline-flex items-center space-x-2 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 px-4 py-2 rounded-full text-sm font-medium mb-6"
                    >
                        <Star className="w-4 h-4" />
                        <span>{t('highlights.signatureExperience')}</span>
                    </motion.div>

                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold mb-6 leading-tight">
                        {t('highlights.title')} <span className="text-gradient">{t('highlights.subtitle')}</span>
                    </h2>
                    <p className="text-lg md:text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed font-light">
                        {t('highlights.description')}
                    </p>

                    {/* Trust Indicators */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={inView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.8, delay: 0.4 }}
                        className="flex justify-center items-center space-x-8 mt-12 flex-wrap gap-4"
                    >
                        <div className="flex items-center space-x-2 bg-white dark:bg-gray-800 px-4 py-2 rounded-full shadow-lg">
                            <Award className="w-5 h-5 text-primary-600" />
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('highlights.stats.awardWinner')}</span>
                        </div>
                        <div className="flex items-center space-x-2 bg-white dark:bg-gray-800 px-4 py-2 rounded-full shadow-lg">
                            <Users className="w-5 h-5 text-accent-600" />
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('highlights.stats.customers')}</span>
                        </div>
                        <div className="flex items-center space-x-2 bg-white dark:bg-gray-800 px-4 py-2 rounded-full shadow-lg">
                            <Clock className="w-5 h-5 text-secondary-600" />
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('highlights.stats.experience')}</span>
                        </div>
                    </motion.div>
                </motion.div>

                {/* Enhanced Highlights Cards */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
                    {highlights.map((highlight, index) => (
                        <motion.div
                            key={highlight.title}
                            initial={{ opacity: 0, y: 30 }}
                            animate={inView ? { opacity: 1, y: 0 } : {}}
                            transition={{ duration: 0.6, delay: index * 0.2 }}
                            className="group"
                        >
                            <div className="relative bg-white dark:bg-gray-800 rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-4 border border-gray-100 dark:border-gray-700">
                                {/* Background Pattern */}
                                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary-100 to-accent-100 dark:from-primary-900/20 dark:to-accent-900/20 rounded-full -translate-y-16 translate-x-16 opacity-50"></div>

                                {/* Image Section */}
                                <div className="relative h-72 overflow-hidden">
                                    <img
                                        src={highlight.image}
                                        alt={highlight.title}
                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

                                    {/* Floating Icon */}
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.8 }}
                                        animate={inView ? { opacity: 1, scale: 1 } : {}}
                                        transition={{ duration: 0.5, delay: index * 0.2 + 0.3 }}
                                        className="absolute top-6 right-6 w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center shadow-2xl group-hover:scale-105 transition-transform duration-300"
                                    >
                                        <highlight.icon className="w-8 h-8 text-white" />
                                        <div className="absolute inset-0 bg-white/10 rounded-2xl"></div>
                                    </motion.div>

                                    {/* Number Badge */}
                                    <div className="absolute top-6 left-6 w-10 h-10 bg-white/90 dark:bg-gray-900/90 rounded-full flex items-center justify-center">
                                        <span className="text-sm font-bold text-gray-900 dark:text-white">0{index + 1}</span>
                                    </div>

                                    {/* Bottom Gradient Overlay */}
                                    <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white dark:from-gray-800 to-transparent"></div>
                                </div>

                                {/* Content Section */}
                                <div className="relative p-8 -mt-12 z-10">
                                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-100 dark:border-gray-700">
                                        <h3 className="text-xl font-serif font-bold mb-3 text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-300">
                                            {highlight.title}
                                        </h3>
                                        <p className="text-gray-600 dark:text-gray-300 leading-relaxed text-base mb-4">
                                            {highlight.description}
                                        </p>

                                        {/* Progress Bar */}
                                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1">
                                            <motion.div
                                                initial={{ width: 0 }}
                                                animate={inView ? { width: '100%' } : {}}
                                                transition={{ duration: 1, delay: index * 0.2 + 0.8 }}
                                                className="h-1 bg-gradient-to-r from-primary-500 to-accent-500 rounded-full"
                                            />
                                        </div>
                                    </div>

                                    {/* Decorative Element */}
                                    <div className="absolute -bottom-2 -right-2 w-6 h-6 bg-gradient-to-br from-accent-400 to-accent-600 rounded-full opacity-60"></div>
                                </div>

                                {/* Hover Glow Effect */}
                                <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-primary-500/10 to-accent-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Enhanced Stats Section */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.6, delay: 0.6 }}
                    className="mt-24"
                >
                    <div className="bg-white dark:bg-gray-800 rounded-3xl p-12 shadow-2xl border border-gray-100 dark:border-gray-700 relative overflow-hidden">
                        {/* Background Pattern */}
                        <div className="absolute inset-0 opacity-5">
                            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-primary-500 to-accent-500"></div>
                            <div className="absolute top-0 left-0 w-full h-full" style={{
                                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2030/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
                            }}></div>
                        </div>

                        <div className="relative grid grid-cols-2 md:grid-cols-4 gap-8">
                            {[
                                { value: "4.3", label: t('stats.starRating'), icon: Star, color: "text-yellow-500" },
                                { value: "500+", label: t('stats.happyCustomers'), icon: Users, color: "text-primary-600" },
                                { value: "50+", label: t('stats.signatureDishes'), icon: Utensils, color: "text-accent-600" },
                                { value: "5", label: t('stats.yearsExcellence'), icon: Award, color: "text-secondary-600" }
                            ].map((stat, index) => (
                                <motion.div
                                    key={stat.label}
                                    initial={{ opacity: 0, y: 30, scale: 0.8 }}
                                    animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
                                    transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
                                    className="text-center group"
                                >
                                    <div className="flex flex-col items-center space-y-4">
                                        <div className="relative">
                                            <div className="w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg">
                                                <stat.icon className={`w-8 h-8 ${stat.color}`} />
                                            </div>
                                            <div className="absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-br from-primary-400 to-accent-400 rounded-full opacity-80"></div>
                                        </div>
                                        <div>
                                            <motion.div
                                                initial={{ scale: 0 }}
                                                animate={inView ? { scale: 1 } : {}}
                                                transition={{ duration: 0.8, delay: 1 + index * 0.1 }}
                                                className="text-3xl md:text-4xl font-bold bg-gradient-to-br from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mb-2"
                                            >
                                                {stat.value}
                                            </motion.div>
                                            <div className="text-sm md:text-base font-medium text-gray-600 dark:text-gray-400 leading-tight">
                                                {stat.label}
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>

                        {/* Decorative elements */}
                        <div className="absolute top-8 right-8 w-4 h-4 bg-gradient-to-br from-primary-400 to-accent-400 rounded-full opacity-60"></div>
                        <div className="absolute bottom-8 left-8 w-6 h-6 bg-gradient-to-br from-accent-400 to-secondary-400 rounded-full opacity-40"></div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default Highlights;
